/**
 * Canonical financial-platform semantics shared by descriptors, APIs, reports,
 * persistence adapters, and jurisdiction packages.
 *
 * These values are intentionally presentation-agnostic. Backend descriptor
 * resolvers decide which values and capabilities an authenticated actor may
 * receive; frontend code must not infer access from these contracts.
 */
export const FINANCIAL_BOOKS = Object.freeze([
    'INVESTMENT_BOOK',
    'ACCOUNTING_BOOK',
    'TAX_BOOK',
    'CUSTODY_BOOK',
    'CLIENT_REPORTING_BOOK',
    'REGULATORY_BOOK',
]);
export const POSITION_BASIS = Object.freeze([
    'TRADE_DATE',
    'SETTLEMENT_DATE',
    'CONTRACTUAL',
    'SETTLED',
    'AVAILABLE',
    'RESTRICTED',
    'PLEDGED',
]);
export const CASH_BASIS = Object.freeze([
    'PROJECTED',
    'SETTLED',
    'ACCOUNTING',
    'CLIENT_PUBLISHED',
]);
export const FINANCIAL_DATA_FRESHNESS = Object.freeze([
    'CURRENT',
    'DELAYED',
    'STALE',
    'PARTIAL',
    'UNAVAILABLE',
]);
export const CLIENT_FIELD_CLASSIFICATIONS = Object.freeze([
    'CLIENT_VISIBLE',
    'CLIENT_VISIBLE_MASKED',
    'CLIENT_VISIBLE_AGGREGATED',
    'INTERNAL_OPERATIONAL',
    'INTERNAL_CONTROL',
    'REGULATORY_RESTRICTED',
    'PLATFORM_RESTRICTED',
]);
export const FINANCIAL_CAPABILITY_DOMAINS = Object.freeze([
    'PLATFORM_ADMINISTRATION',
    'ORGANIZATION_ADMINISTRATION',
    'REFERENCE_DATA',
    'MARKET_DATA',
    'PORTFOLIO_MANAGEMENT',
    'ORDER_MANAGEMENT',
    'TRADE_MANAGEMENT',
    'TRANSACTION_MANAGEMENT',
    'POSITION_MANAGEMENT',
    'SETTLEMENT',
    'CUSTODY',
    'CORPORATE_ACTIONS',
    'ACCOUNTING',
    'PERFORMANCE',
    'RISK',
    'INVESTMENT_COMPLIANCE',
    'RECONCILIATION',
    'TAX',
    'CLIENT_REPORTING',
    'DOCUMENT_EVIDENCE',
    'REGULATORY_REPORTING',
]);
// ---------------------------------------------------------------------------
// §9.2 Instrument lifecycle operations
// ---------------------------------------------------------------------------
export const INSTRUMENT_LIFECYCLE_OPERATIONS = Object.freeze([
    'CREATE_DRAFT',
    'UPDATE_DRAFT',
    'SUBMIT',
    'APPROVE',
    'REJECT',
    'PUBLISH',
    'CORRECT',
    'MERGE',
    'UNMERGE',
    'SUSPEND',
    'RETIRE',
    'RESTORE',
]);
// ---------------------------------------------------------------------------
// §11.1 Order and trade lifecycle pipeline
// ---------------------------------------------------------------------------
export const ORDER_TRADE_LIFECYCLE_STAGES = Object.freeze([
    'INVESTMENT_DECISION',
    'ORDER',
    'ALLOCATION',
    'EXECUTION',
    'TRADE',
    'CONFIRMATION',
    'SETTLEMENT_INSTRUCTION',
    'SETTLEMENT',
    'CUSTODY_MOVEMENT',
    'ACCOUNTING_POSTING',
    'RECONCILIATION',
]);
// ---------------------------------------------------------------------------
// §14.1 Corporate action lifecycle
// ---------------------------------------------------------------------------
export const CORPORATE_ACTION_LIFECYCLE_STAGES = Object.freeze([
    'ANNOUNCED',
    'CONFIRMED',
    'ENTITLEMENT_CALCULATED',
    'ELECTION_OPEN',
    'INSTRUCTION_DEADLINE',
    'ELECTION_RECEIVED',
    'CUSTODIAN_INSTRUCTED',
    'CONFIRMED_BY_CUSTODIAN',
    'ALLOCATED',
    'PAYMENT_RECEIVED',
    'SECURITIES_RECEIVED',
    'WITHHOLDING_TAX_APPLIED',
    'TRANSACTIONS_CREATED',
    'ACCOUNTING_POSTED',
    'RECONCILED',
    'REVISED',
    'CANCELLED',
]);
// ---------------------------------------------------------------------------
// §16.1 Performance methodologies
// ---------------------------------------------------------------------------
export const PERFORMANCE_METHODOLOGIES = Object.freeze([
    'TIME_WEIGHTED_RETURN',
    'MONEY_WEIGHTED_RETURN',
    'INTERNAL_RATE_OF_RETURN',
    'GROSS_OF_FEE',
    'NET_OF_FEE',
    'PRE_TAX',
    'POST_TAX',
    'DAILY',
    'MONTHLY',
    'QUARTERLY',
    'FISCAL_YEAR',
    'ANNUALIZED',
    'SINCE_INCEPTION',
    'BENCHMARK',
    'ACTIVE_RETURN',
    'CONTRIBUTION',
    'ATTRIBUTION',
    'COMPOSITE',
]);
// ---------------------------------------------------------------------------
// §17.1 Risk measures
// ---------------------------------------------------------------------------
export const RISK_MEASURES = Object.freeze([
    'ISSUER_CONCENTRATION',
    'SECTOR_CONCENTRATION',
    'CURRENCY_EXPOSURE',
    'COUNTRY_EXPOSURE',
    'COUNTERPARTY_EXPOSURE',
    'INSTRUMENT_TYPE_EXPOSURE',
    'DURATION',
    'CONVEXITY',
    'INTEREST_RATE_SENSITIVITY',
    'LIQUIDITY',
    'VOLATILITY',
    'DRAWDOWN',
    'BETA',
    'TRACKING_ERROR',
    'VALUE_AT_RISK',
    'SCENARIO_ANALYSIS',
    'STRESS_TESTING',
    'RISK_CONTRIBUTION',
    'LOOK_THROUGH_EXPOSURE',
    'STALE_PRICE_EXPOSURE',
    'MODEL_RISK',
    'VALUATION_SOURCE_RISK',
]);
// ---------------------------------------------------------------------------
// §12.1 Transaction model distinctions
// ---------------------------------------------------------------------------
export const TRANSACTION_VARIANT_TYPES = Object.freeze([
    'ORIGINAL',
    'CORRECTION',
    'REVERSAL',
    'REPLACEMENT',
]);
// ---------------------------------------------------------------------------
// §19.2 Reconciliation break model
// ---------------------------------------------------------------------------
export const RECONCILIATION_BREAK_STATUSES = Object.freeze([
    'OPEN',
    'INVESTIGATING',
    'PENDING_ADJUSTMENT',
    'ADJUSTED',
    'WAIVED',
    'ESCALATED',
    'CLOSED',
]);
// ---------------------------------------------------------------------------
// §35 Dashboard persona family categories
// ---------------------------------------------------------------------------
export const PLATFORM_ROOT_DASHBOARD_CATEGORIES = Object.freeze([
    'ORGANIZATION_COUNT',
    'BRANCH_COUNT',
    'MODULE_ADOPTION',
    'PLATFORM_JOBS',
    'SERVICE_HEALTH',
    'OUTBOX_BACKLOG',
    'SECURITY_POSTURE',
    'DELEGATION_ACTIVITY',
    'PLATFORM_AUDIT',
    'INSTRUMENTS_BY_STATE',
    'PENDING_APPROVALS',
    'DUPLICATE_CANDIDATES',
    'UNMAPPED_IDENTIFIERS',
    'INCOMPLETE_INSTRUMENTS',
    'PROVIDER_HEALTH',
    'STALE_PRICES',
    'MISSING_PRICES',
    'PRICE_CONFLICTS',
    'FX_FRESHNESS',
    'BENCHMARK_FRESHNESS',
    'FAILED_FEEDS',
    'RECALCULATION_BACKLOG',
]);
export const ORG_ADMIN_DASHBOARD_CATEGORIES = Object.freeze([
    'ASSETS_UNDER_MANAGEMENT',
    'MARKET_VALUE',
    'CASH',
    'NET_FLOWS',
    'GAIN_OR_LOSS',
    'PORTFOLIO_COUNT',
    'BENCHMARK_PERFORMANCE',
    'PENDING_APPROVALS',
    'FAILED_IMPORTS',
    'SETTLEMENT_FAILURES',
    'RECONCILIATION_BREAKS',
    'STALE_PRICES',
    'CORPORATE_ACTION_EXCEPTIONS',
    'REPORT_FAILURES',
    'CLOSE_BLOCKERS',
    'CONCENTRATION',
    'LIQUIDITY',
    'LIMIT_UTILIZATION',
    'ACTIVE_BREACHES',
    'PASSIVE_BREACHES',
    'STALE_PRICE_EXPOSURE',
    'MEMBERSHIP_CHANGES',
    'ACCESS_CHANGES',
    'MODULE_STATUS',
    'AUDIT_ACTIVITY',
    'DELEGATION_STATUS',
]);
export const ORG_CLIENT_DASHBOARD_CATEGORIES = Object.freeze([
    'MARKET_VALUE',
    'COST_BASIS',
    'CASH',
    'REALIZED_GAIN_OR_LOSS',
    'UNREALIZED_GAIN_OR_LOSS',
    'ACCRUED_INCOME',
    'PERFORMANCE',
    'BENCHMARK',
    'ALLOCATION',
    'CONCENTRATION',
    'HOLDINGS',
    'TRANSACTIONS',
    'PENDING_SETTLEMENTS',
    'INCOME',
    'CORPORATE_ACTIONS',
    'INSTRUMENT_DETAILS',
    'MARKET_PRICES',
    'RISK_SUMMARY',
    'TAX_SUMMARY',
    'REPORTS_AND_DOCUMENTS',
]);
// ---------------------------------------------------------------------------
// §24.3 Maintenance delegation capabilities
// ---------------------------------------------------------------------------
export const DELEGATION_CAPABILITIES = Object.freeze([
    'ORG_CONFIG_VIEW',
    'ORG_CONFIG_MANAGE',
    'ORG_IDENTITY_VIEW',
    'ORG_IDENTITY_MANAGE',
    'ORG_AUDIT_VIEW',
    'ORG_DATA_VIEW',
    'ORG_DATA_CORRECT',
    'ORG_IMPORT_RETRY',
    'ORG_RECONCILIATION_VIEW',
    'ORG_RECONCILIATION_MANAGE',
    'ORG_REPORT_VIEW',
    'ORG_REPORT_RUN',
    'ORG_FINANCIAL_EXPORT',
    'ORG_ACCESS_INSPECT',
]);
export const DELEGATION_PROHIBITED_ACTIONS = Object.freeze([
    'EDIT_DERIVED_HOLDINGS',
    'DELETE_TRANSACTION_HISTORY',
    'ERASE_AUDIT',
    'BYPASS_APPROVAL',
    'EXPAND_OWN_ACCESS',
    'APPROVE_OWN_DELEGATION',
    'EXTEND_OWN_GRANT',
    'MODIFY_GOVERNING_POLICY',
]);
// ---------------------------------------------------------------------------
// §24.5 Delegated session lifecycle
// ---------------------------------------------------------------------------
export const DELEGATION_SESSION_START_STEPS = Object.freeze([
    'VALIDATE_GRANT',
    'VALIDATE_SCOPE',
    'VALIDATE_EXPIRATION',
    'VALIDATE_AAL',
    'CREATE_SEPARATE_SESSION',
    'ASSIGN_DELEGATE_PERSONA',
    'CLEAR_CACHES',
    'LOAD_DELEGATED_DESCRIPTORS',
    'DISPLAY_PERSISTENT_BANNER',
    'RECORD_AUDIT',
    'REDIRECT_SAFELY',
]);
export const DELEGATION_SESSION_END_STEPS = Object.freeze([
    'TERMINATE_DELEGATED_SESSION',
    'CLEAR_ORGANIZATION_DATA',
    'REMOVE_DELEGATED_DESCRIPTORS',
    'INVALIDATE_SIGNED_URLS',
    'RESTORE_PLATFORM_SHELL',
    'RECORD_EXIT',
    'PREVENT_BROWSER_BACK_DISCLOSURE',
]);
export function validateDelegationGrant(grant) {
    const errors = [];
    if (!grant.grantId.trim())
        errors.push('grantId is required');
    if (!grant.targetOrganizationId.trim())
        errors.push('targetOrganizationId is required');
    if (!grant.purpose.trim())
        errors.push('purpose is required');
    if (!grant.justification.trim())
        errors.push('justification is required');
    if (grant.requestedCapabilities.length === 0)
        errors.push('at least one capability is required');
    if (grant.expiresAt <= grant.startAt)
        errors.push('expiresAt must be after startAt');
    if (grant.status === 'ACTIVE' && !grant.approvedAt)
        errors.push('active grant requires approvedAt');
    if (grant.status === 'REVOKED' && !grant.revokedAt)
        errors.push('revoked grant requires revokedAt');
    if (grant.status === 'REVOKED' && !grant.revokedBy)
        errors.push('revoked grant requires revokedBy');
    for (const capability of grant.requestedCapabilities) {
        if (!DELEGATION_CAPABILITIES.includes(capability)) {
            errors.push(`unknown delegation capability: ${capability}`);
        }
    }
    return errors;
}
export function validateRegulatoryLifecycleContract(contract) {
    const errors = [];
    if (!contract.jurisdiction.trim())
        errors.push('jurisdiction is required');
    if (!contract.regulator.trim())
        errors.push('regulator is required');
    if (!contract.regulatedEntityType.trim())
        errors.push('regulatedEntityType is required');
    if (!contract.ruleId.trim())
        errors.push('ruleId is required');
    if (!contract.ruleVersion.trim())
        errors.push('ruleVersion is required');
    if (!contract.sourceAuthority.trim())
        errors.push('sourceAuthority is required');
    if (!contract.effectiveFrom.trim())
        errors.push('effectiveFrom is required');
    if (contract.effectiveTo && contract.effectiveTo < contract.effectiveFrom) {
        errors.push('effectiveTo precedes effectiveFrom');
    }
    return errors;
}
// ---------------------------------------------------------------------------
// §37.2 Date type distinctions
// ---------------------------------------------------------------------------
export const FINANCIAL_DATE_TYPES = Object.freeze([
    'TRADE_DATE',
    'SETTLEMENT_DATE',
    'POSTING_DATE',
    'EFFECTIVE_DATE',
    'VALUATION_DATE',
    'ACCOUNTING_DATE',
    'REPORT_AS_OF_DATE',
    'GENERATION_DATE',
]);
// ---------------------------------------------------------------------------
// §38 Empty state types
// ---------------------------------------------------------------------------
export const EMPTY_STATE_TYPES = Object.freeze([
    'NO_DATA_CREATED',
    'NO_FILTER_MATCH',
    'NO_PERIOD_ACTIVITY',
    'NO_ASSIGNED_DATA',
    'PROCESSING_PENDING',
    'UPSTREAM_FAILURE',
    'READ_ONLY_ACCESS',
]);
